import { useEffect, useState } from 'react';
import axios from 'axios';
import BASE_URL from '../../config';
import './milestoneList.css';
import ConfirmDialog from '../../components/alerts/confirmDialog';

// Matches milestoneForm.js's UK_POSTCODE_RE. A milestone's address text can
// itself end in a UK postcode (e.g. it was filled in from a map-search
// result, which returns a full formatted address including one) -- in that
// case, also appending the separately-stored postcode field produced a
// confusing "...SM1 2QW, United Kingdom, SM1 2RB"-style string with what
// look like two different postcodes for the same place (reported by a
// customer). Prefer the address text alone when it already has one.
const UK_POSTCODE_RE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i;

function formatLocation(m) {
  const address = (m.address || '').trim();
  const postcode = (m.postcode || '').trim();
  if (address && UK_POSTCODE_RE.test(address)) {
    return address;
  }
  return [address, postcode].filter(Boolean).join(', ');
}

export default function MilestoneList({ uid }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [editForm, setEditForm] = useState({ address: '', postcode: '', latitude: '', longitude: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const token = localStorage.getItem('jwt_token');

const getMilestonesByUid = async (uid) => {
  const res = await axios.get(`${BASE_URL}/api/get-milestone-by-uid/`, {
    params: { uid },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

const deleteMilestone = async (uid, name) => {
  try {
    const response = await fetch(`${BASE_URL}/api/delete-milestone/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ uid, name }),
    });

    if (response.status === 204) {
      return { success: true, message: 'Milestone deleted successfully' };
    } else {
      const errorData = await response.json();
      return { success: false, error: errorData.error || 'Unknown error' };
    }
  } catch (error) {
    return { success: false, error: 'Network error or server is unreachable' };
  }
};

// PATCH an existing milestone (already supported by the backend --
// mavis_backend/milestone/views.py:edit_milestone -- just not wired up to
// any UI before this; the list only had Delete, so the only way to fix a
// wrong address/postcode/lat/lng was delete + re-add from scratch).
// `name` is required by the backend to look the record up, and isn't
// editable here to keep that lookup unambiguous.
const patchMilestone = async (payload) => {
  const response = await axios.patch(`${BASE_URL}/api/edit-milestone/`, payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
  });
  return response.data;
};


  useEffect(() => {
    const fetchMilestones = async () => {
      setLoading(true);
      try {
        const data = await getMilestonesByUid(uid);
        setMilestones(data);
      } catch (error) {
        console.error('Error fetching milestones:', error);
      } finally {
        setLoading(false);
      }
    };

    if (uid) {
      fetchMilestones();
    } else {
      setMilestones([]);
    }
  }, [uid]);

  const handleDelete = async () => {
    if (milestoneToDelete) {
      const result = await deleteMilestone(milestoneToDelete.uid, milestoneToDelete.name);
      if (result.success) {
        setMilestones(prevMilestones => prevMilestones.filter(m => m.name !== milestoneToDelete.name));
      } else {
        alert('Error deleting milestone: ' + result.error);
      }
      setConfirmVisible(false);
    }
  };

  const handleConfirmDelete = (milestone) => {
    setMilestoneToDelete(milestone);
    setConfirmVisible(true);
  };

  const handleCancelDelete = () => {
    setConfirmVisible(false);
    setMilestoneToDelete(null);
  };

  const handleEditClick = (m) => {
    setEditingName(m.name);
    setEditForm({
      address: m.address || '',
      postcode: m.postcode || '',
      latitude: m.latitude ?? '',
      longitude: m.longitude ?? '',
    });
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditCancel = () => {
    setEditingName(null);
    setSavingEdit(false);
  };

  const handleEditSave = async (m) => {
    setSavingEdit(true);
    try {
      const updated = await patchMilestone({
        uid,
        name: m.name,
        address: editForm.address,
        postcode: editForm.postcode,
        latitude: editForm.latitude,
        longitude: editForm.longitude,
      });
      setMilestones((prev) => prev.map((item) => (item.name === m.name ? updated : item)));
      setEditingName(null);
    } catch (error) {
      alert('Error updating milestone: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="unique-milestone-list" role="status" aria-live="polite">
        <p className="sr-only">Loading milestones...</p>
      </div>
    );
  }

  return (
    <section className="unique-milestone-list" aria-labelledby="existing-milestones-heading">
      <h3 id="existing-milestones-heading">Existing Milestones</h3>

      <div aria-live="polite">
        {milestones.length === 0 ? (
          <p className="milestone-empty-state">No milestones found. Add one above.</p>
        ) : (
          <ul aria-label="Milestones list">
            {milestones.map((m, idx) => {
              const isEditing = editingName === m.name;
              const locationText = formatLocation(m);
              return (
                <li key={idx} className="unique-milestone-item">
                  {isEditing ? (
                    <div className="unique-milestone-edit-form">
                      <label htmlFor={`edit-address-${idx}`} className="sr-only">Address for {m.name}</label>
                      <input
                        id={`edit-address-${idx}`}
                        name="address"
                        value={editForm.address}
                        onChange={handleEditChange}
                        placeholder="Address"
                        className="unique-form-input"
                        autoComplete="street-address"
                      />
                      <label htmlFor={`edit-postcode-${idx}`} className="sr-only">Postcode for {m.name}</label>
                      <input
                        id={`edit-postcode-${idx}`}
                        name="postcode"
                        value={editForm.postcode}
                        onChange={handleEditChange}
                        placeholder="Postcode"
                        className="unique-form-input"
                        autoComplete="postal-code"
                      />
                      <label htmlFor={`edit-latitude-${idx}`} className="sr-only">Latitude for {m.name}</label>
                      <input
                        id={`edit-latitude-${idx}`}
                        name="latitude"
                        value={editForm.latitude}
                        onChange={handleEditChange}
                        placeholder="Latitude"
                        className="unique-form-input"
                        inputMode="decimal"
                      />
                      <label htmlFor={`edit-longitude-${idx}`} className="sr-only">Longitude for {m.name}</label>
                      <input
                        id={`edit-longitude-${idx}`}
                        name="longitude"
                        value={editForm.longitude}
                        onChange={handleEditChange}
                        placeholder="Longitude"
                        className="unique-form-input"
                        inputMode="decimal"
                      />
                      <div className="unique-milestone-edit-actions">
                        <button
                          type="button"
                          className="unique-save-btn"
                          onClick={() => handleEditSave(m)}
                          disabled={savingEdit}
                          aria-label={`Save changes to ${m.name}`}
                        >
                          {savingEdit ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="unique-cancel-btn"
                          onClick={handleEditCancel}
                          disabled={savingEdit}
                          aria-label={`Cancel editing ${m.name}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>{m.name}</strong>
                        {locationText ? (
                          <span> - {locationText}</span>
                        ) : (
                          <span aria-label={`Latitude ${m.latitude}, Longitude ${m.longitude}`}>
                            {' '}- Lat: {m.latitude}, Lng: {m.longitude}
                          </span>
                        )}
                      </div>
                      <div className="unique-milestone-item-actions">
                        <button
                          className="unique-edit-btn"
                          onClick={() => handleEditClick(m)}
                          aria-label={`Edit milestone ${m.name}`}
                        >
                          Edit
                        </button>
                        <button
                          className="unique-delete-btn"
                          onClick={() => handleConfirmDelete(m)}
                          aria-label={`Delete milestone ${m.name}`}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmDialog
        visible={confirmVisible}
        onConfirm={handleDelete}
        onCancel={handleCancelDelete}
        message={`Are you sure you want to delete the milestone: ${milestoneToDelete?.name}?`}
      />
    </section>
  );
}
